import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const SHOW_AFTER_PX = 56;
const MIN_DELTA_PX = 6;
const BOTTOM_OFFSET_PX = 48;

function getScrollY() {
  return window.scrollY || document.documentElement.scrollTop || 0;
}

function pageCanScroll() {
  return document.documentElement.scrollHeight > window.innerHeight + 8;
}

function isNearDocumentBottom() {
  const y = getScrollY();
  const max = document.documentElement.scrollHeight - window.innerHeight;
  return max > 0 && y >= max - BOTTOM_OFFSET_PX;
}

/**
 * Hidden at the top of a scrollable page. Shown after scrolling down.
 * Hidden again while scrolling up. Always shown when the page cannot
 * scroll, when near the document end, or when `forceVisible` is true.
 *
 * @param {{ forceVisible?: boolean }} [options]
 * @returns {boolean}
 */
export function useScrollDirectionVisibility({ forceVisible = false } = {}) {
  const { pathname } = useLocation();
  const [isVisible, setIsVisible] = useState(() =>
    typeof window === 'undefined' ? true : !pageCanScroll(),
  );

  useEffect(() => {
    let lastY = getScrollY();
    let ticking = false;

    const apply = () => {
      if (forceVisible || !pageCanScroll() || isNearDocumentBottom()) {
        setIsVisible(true);
        lastY = getScrollY();
        ticking = false;
        return;
      }

      const y = getScrollY();
      const delta = y - lastY;

      if (y <= SHOW_AFTER_PX) {
        setIsVisible(false);
      } else if (delta > MIN_DELTA_PX) {
        setIsVisible(true);
      } else if (delta < -MIN_DELTA_PX) {
        setIsVisible(false);
      }

      lastY = y;
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) {
        return;
      }
      ticking = true;
      window.requestAnimationFrame(apply);
    };

    apply();
    const retryId = window.setTimeout(apply, 250);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.clearTimeout(retryId);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [forceVisible, pathname]);

  return forceVisible || isVisible;
}
